import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import {
  Button,
  Modal,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  secondaryTextClassName,
  useAuth,
  type AuthFormError,
} from '@monecolier/ui';
import {
  getAvailablePaymentMethods,
  submitPaymentProof,
  type SchoolPaymentMethodType,
} from '@monecolier/api-client';
import { capacitorPhotoToFile } from '../../lib/capacitorPhoto';

// PAY-16 — dépôt d'une preuve de paiement par le parent, PURE TRAÇABILITÉ
// (voir apps/api/src/routes/paymentProofs.ts pour la règle métier gravée
// côté backend : jamais de Payment/Invoice créé ici). Reproduit EXACTEMENT
// le pattern déjà établi par `NewSupplyOrderModal.tsx` (FOU-03) : `Modal`
// (@monecolier/ui, UX-22) en mode "conteneur pur" (`onConfirm` omis), le
// composant charge LUI-MÊME ses données au montage plutôt que de les
// recevoir en props (même raison : `Home.tsx` ne charge pas déjà cette
// donnée pour un autre besoin).
//
// Champ fichier : réutilise TEL QUEL le mécanisme d'upload ENR-07
// (`accept="application/pdf,image/jpeg,image/png"`, mêmes limites
// serveur — voir `apps/api/src/lib/paymentProof.ts`, alias des constantes
// ENR-07, aucune nouvelle limite inventée), même précédent visuel que
// `EnrollmentDocumentRequirementRow.tsx`.
//
// Destination revendiquée : `GET /parents/students/:id/payment-methods`
// (PAY-18, anticipé pour ce ticket précis) — UNIQUEMENT les comptes
// RÉELLEMENT configurés par CETTE école (jamais un compte générique de la
// plateforme, exigence explicite du ticket) ; le sélecteur reste OPTIONNEL
// et disparaît entièrement si l'école n'a encore configuré AUCUN moyen.
//
// MOB-05 — ajoute la capture caméra native (`@capacitor/camera`) EN PLUS de
// l'`<input type="file">` ci-dessus, jamais à sa place : le résultat de la
// capture (`lib/capacitorPhoto.ts#capacitorPhotoToFile()`) alimente le MÊME
// état `file`, consommé par le MÊME `submitPaymentProof()` inchangé — seule
// la SOURCE du fichier change, aucune nouvelle règle de validation.

const DESTINATION_LABEL_KEYS: Record<SchoolPaymentMethodType, string> = {
  WAVE: 'depositPaymentProofModal.destinationWave',
  ORANGE_MONEY: 'depositPaymentProofModal.destinationOrangeMoney',
  PSP_CARD: 'depositPaymentProofModal.destinationPspCard',
};

export interface DepositPaymentProofModalProps {
  studentId: string;
  onCancel: () => void;
  onSubmitted: (message: string) => void;
}

export function DepositPaymentProofModal({ studentId, onCancel, onSubmitted }: DepositPaymentProofModalProps) {
  const { accessToken, apiClient } = useAuth();
  const { t } = useTranslation();

  const [availableDestinations, setAvailableDestinations] = useState<SchoolPaymentMethodType[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [destination, setDestination] = useState<SchoolPaymentMethodType | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AuthFormError | null>(null);
  const [capturingPhoto, setCapturingPhoto] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    getAvailablePaymentMethods(apiClient, studentId, accessToken)
      .then((response) => {
        const enabled = (Object.keys(response.availableMethods) as SchoolPaymentMethodType[]).filter(
          (type) => response.availableMethods[type],
        );
        setAvailableDestinations(enabled);
      })
      .catch(() => setAvailableDestinations([]));
    // Chargement UNIQUE au montage — même précédent que
    // `NewSupplyOrderModal.tsx` (FND-21).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MOB-05 — visible UNIQUEMENT dans la coquille native (voir le rendu
  // conditionnel plus bas, `Capacitor.isNativePlatform()`, même mécanisme
  // déjà utilisé par `usePushNotificationRegistration.ts`, MOB-06) : jamais
  // proposé dans un navigateur web classique, où l'API caméra Capacitor n'a
  // structurellement aucun sens. Un refus de permission (ou toute autre
  // erreur native, y compris une simple annulation par le parent) retombe
  // EN SILENCE sur l'`<input type="file">` déjà affiché juste à côté — jamais
  // un écran bloqué, même philosophie que le refus de permission push
  // (MOB-06).
  async function handleTakePhoto() {
    setCapturingPhoto(true);
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 80,
        saveToGallery: false,
      });
      const photoFile = await capacitorPhotoToFile(photo, `preuve-paiement-${studentId}`);
      setFile(photoFile);
    } catch (err) {
      // Refus de permission ou annulation — jamais bloquant, le parent garde
      // la main sur l'`<input type="file">` classique.
      console.error('[MOB-05] capture caméra impossible, repli sur le fichier existant', err);
    } finally {
      setCapturingPhoto(false);
    }
  }

  async function handleSubmit() {
    if (!accessToken || !file) return;
    setError(null);
    setSubmitting(true);
    try {
      await submitPaymentProof(apiClient, studentId, file, accessToken, {
        note: note.trim() ? note.trim() : undefined,
        claimedDestinationType: destination ? destination : undefined,
      });
      onSubmitted(t('depositPaymentProofModal.submitted'));
    } catch (err) {
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('depositPaymentProofModal.title')} onCancel={onCancel}>
      <div className="flex flex-col gap-4">
        <p className={secondaryTextClassName}>{t('depositPaymentProofModal.intro')}</p>

        {error ? (
          <p role="alert" data-testid="deposit-payment-proof-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}

        <label className={formFieldClassName}>
          {t('depositPaymentProofModal.fileLabel')}
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            disabled={submitting}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            className={formInputClassName}
            data-testid="deposit-payment-proof-file"
          />
        </label>

        {Capacitor.isNativePlatform() ? (
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => void handleTakePhoto()}
            disabled={submitting || capturingPhoto}
            data-testid="deposit-payment-proof-camera"
          >
            {capturingPhoto ? t('depositPaymentProofModal.submitting') : t('depositPaymentProofModal.takePhoto')}
          </Button>
        ) : null}

        <label className={formFieldClassName}>
          {t('depositPaymentProofModal.noteLabel')}
          <input
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={submitting}
            className={formInputClassName}
            data-testid="deposit-payment-proof-note"
          />
        </label>

        {availableDestinations && availableDestinations.length > 0 ? (
          <label className={formFieldClassName}>
            {t('depositPaymentProofModal.destinationLabel')}
            <select
              value={destination}
              onChange={(event) => setDestination(event.target.value as SchoolPaymentMethodType | '')}
              disabled={submitting}
              className={formInputClassName}
              data-testid="deposit-payment-proof-destination"
            >
              <option value="">{t('depositPaymentProofModal.destinationUnknown')}</option>
              {availableDestinations.map((type) => (
                <option key={type} value={type}>
                  {t(DESTINATION_LABEL_KEYS[type])}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!file || submitting}
          data-testid="deposit-payment-proof-submit"
        >
          {submitting ? t('depositPaymentProofModal.submitting') : t('depositPaymentProofModal.submit')}
        </Button>
      </div>
    </Modal>
  );
}
